import { ClipboardList, Pencil, Plus, Power, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import type { ServiceChecklist } from '@obraplan/core/domain/quality/service-checklist';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import {
  ChecklistFormDialog,
  type ChecklistFormSubmit,
} from '@/features/quality/ChecklistFormDialog';
import {
  useContainer,
  usePermissionGlobally,
  useSessionState,
} from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/**
 * Catálogo de checklists de qualidade — as FVS (Fase 19, fatia 5).
 *
 * Global, em Configurações, ao lado do de disciplinas: a ficha de alvenaria é a mesma em
 * toda obra. Onde ela é respondida é a medição do dia, na obra que cadastrou o serviço.
 */
export function ChecklistsPage() {
  const container = useContainer();
  const { session } = useSessionState();
  const canManage = usePermissionGlobally(PERMISSION.STRUCTURE_MANAGE);

  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceChecklist | undefined>(undefined);
  // `?ficha=<id>` é o link do cadastro do serviço (RN-297): abre a ficha, uma vez.
  const [params, setParams] = useSearchParams();
  const fichaPedida = params.get('ficha');

  const catalog = useLive(
    () =>
      session === undefined
        ? Promise.resolve(undefined)
        : container.listServiceChecklists.execute({}, session),
    [session?.userId],
  );

  const checklists = useMemo(
    () => (catalog !== undefined && catalog.ok ? catalog.value.checklists : []),
    [catalog],
  );
  const services = useMemo(
    () => (catalog !== undefined && catalog.ok ? catalog.value.services : []),
    [catalog],
  );
  const disciplines = useMemo(
    () => (catalog !== undefined && catalog.ok ? catalog.value.disciplines : []),
    [catalog],
  );

  /**
   * `ALVENARIA > ASSENTAMENTO DE ALVENARIA` — o serviço por extenso, a partir do id.
   *
   * A ficha guarda id desde a Fase 21 (ADR-138); quem lê a tela continua querendo o nome, e
   * é aqui que a volta acontece. Id sem serviço no cadastro aparece como `—` em vez de sumir:
   * o vínculo perdido tem de dar na vista, que é o que a ligação por nome não fazia.
   */
  const descreverServico = useMemo(() => {
    const disciplinaPorId = new Map(disciplines.map((item) => [item.id as string, item.name]));
    const porId = new Map(
      services.map((service) => [
        service.id as string,
        `${disciplinaPorId.get(service.disciplineId) ?? '—'} > ${service.name}`,
      ]),
    );
    return (serviceId: string): string => porId.get(serviceId) ?? '—';
  }, [services, disciplines]);

  const filtered = useMemo(() => {
    const term = normalizeName(search);
    if (term === '') return checklists;
    return checklists.filter(
      (checklist) =>
        normalizeName(checklist.name).includes(term) ||
        normalizeName(checklist.code ?? '').includes(term) ||
        checklist.serviceIds.some((serviceId) =>
          normalizeName(descreverServico(serviceId)).includes(term),
        ),
    );
  }, [checklists, search, descreverServico]);

  // Quem pode editar cai direto na ficha; quem só lê a vê **filtrada**, porque não há
  // diálogo a abrir para ela. O parâmetro sai do endereço depois de usado — recarregar a
  // página não reabre a ficha que já foi fechada.
  useEffect(() => {
    if (fichaPedida === null || catalog === undefined || !catalog.ok) return;
    const ficha = catalog.value.checklists.find((item) => item.id === fichaPedida);
    if (ficha !== undefined) {
      if (canManage) {
        setEditing(ficha);
        setFormOpen(true);
      } else {
        setSearch(ficha.name);
      }
    }
    setParams({}, { replace: true });
  }, [fichaPedida, catalog, canManage, setParams]);

  const closeForm = (): void => {
    setFormOpen(false);
    setEditing(undefined);
  };

  const [save, saveState] = useCommand(
    (input: ChecklistFormSubmit & { checklistId?: string }) =>
      container.saveServiceChecklist.execute(input, session!),
    {
      successMessage: (saved) => `Checklist "${saved.name}" gravada.`,
      silent: true,
      onSuccess: closeForm,
    },
  );

  const [toggleActive] = useCommand(
    (input: { checklistId: string; isActive: boolean }) =>
      container.setServiceChecklistActive.execute(input, session!),
    {
      successMessage: (checklist) =>
        checklist.isActive ? 'Checklist reativada.' : 'Checklist desativada.',
    },
  );

  if (catalog === undefined) return <LoadingState label="Carregando as checklists…" />;
  if (!catalog.ok) return <ErrorState message={catalog.error.message} code={catalog.error.code} />;

  const activeCount = checklists.filter((checklist) => checklist.isActive).length;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Checklists de qualidade</h1>
          <p className="text-(--color-content-muted)">
            As fichas de verificação de serviço, iguais para todas as obras. Cada uma aponta para os
            serviços a que se aplica; a obra que cadastrar o serviço passa a respondê-la na medição.
          </p>
        </div>
        {canManage && (
          <Button
            variant="primary"
            onClick={() => {
              setEditing(undefined);
              setFormOpen(true);
            }}
          >
            <Plus aria-hidden="true" />
            Nova checklist
          </Button>
        )}
      </header>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>
              {activeCount} checklists ativas
              {checklists.length > activeCount &&
                ` · ${String(checklists.length - activeCount)} desativadas`}
            </CardTitle>
            <CardDescription>
              Desativar tira a ficha da próxima medição sem apagar o que já foi respondido.
            </CardDescription>
          </div>
          <div className="relative flex items-center">
            <Search
              className="pointer-events-none absolute left-2.5 size-4 text-(--color-content-muted)"
              aria-hidden="true"
            />
            <Input
              aria-label="Buscar checklist"
              className="w-56 pl-8"
              placeholder="Buscar por nome, código, serviço…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <EmptyState
              title={checklists.length === 0 ? 'Nenhuma checklist' : 'Nenhuma checklist encontrada'}
              description={
                checklists.length === 0
                  ? 'Cadastre a primeira ficha de verificação de serviço.'
                  : 'Nenhuma checklist corresponde à busca.'
              }
              icon={<ClipboardList className="size-8" />}
            />
          ) : (
            <TableWrapper>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Checklist</TableHead>
                    <TableHead>Aplica-se a</TableHead>
                    <TableHead className="text-right">Itens</TableHead>
                    <TableHead>Situação</TableHead>
                    {canManage && (
                      <TableHead className="text-right">
                        <span className="sr-only">Ações</span>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((checklist) => (
                    <TableRow key={checklist.id}>
                      <TableCell className="font-mono text-xs">{checklist.code ?? '—'}</TableCell>
                      <TableCell className="font-medium">{checklist.name}</TableCell>
                      <TableCell className="text-xs text-(--color-content-muted)">
                        {checklist.serviceIds.length === 0
                          ? 'Nenhum serviço'
                          : checklist.serviceIds.map(descreverServico).join(' · ')}
                      </TableCell>
                      <TableCell className="tabular text-right">{checklist.items.length}</TableCell>
                      <TableCell>
                        {checklist.isActive ? (
                          <Badge tone="ok">Ativa</Badge>
                        ) : (
                          <Badge tone="neutral">Desativada</Badge>
                        )}
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Editar ${checklist.name}`}
                              onClick={() => {
                                setEditing(checklist);
                                setFormOpen(true);
                              }}
                            >
                              <Pencil aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={
                                checklist.isActive
                                  ? `Desativar ${checklist.name}`
                                  : `Reativar ${checklist.name}`
                              }
                              onClick={() =>
                                void toggleActive({
                                  checklistId: checklist.id,
                                  isActive: !checklist.isActive,
                                })
                              }
                            >
                              <Power aria-hidden="true" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
          )}
        </CardContent>
      </Card>

      <ChecklistFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          if (!open) closeForm();
          else setFormOpen(true);
        }}
        checklist={editing}
        services={services}
        disciplines={disciplines}
        onSubmit={(input) =>
          void save(editing === undefined ? input : { ...input, checklistId: editing.id })
        }
        isPending={saveState.isPending}
        errorMessage={saveState.error?.message}
      />
    </div>
  );
}
